import { CopyIcon, UploadIcon } from '@radix-ui/react-icons'
import { useParams } from '@tanstack/react-router'
import useMessage from 'antd/es/message/useMessage'
import CantileverButton, {
  AddButton,
  CantileverLinkButton,
} from 'components/CantileverButton'
import CantileverCard from 'components/CantileverCard'
import { Popup } from 'components/Popup'
import { convertDateToString } from 'features/assetDatabase/components/assetTable/AssetTableHelpers'
import {
  InspectionOverviewAdminGate,
  InspectionOverviewViewerGate,
} from 'features/assetDatabase/components/gates/InspectionOverviewGates'
import { useRolodexEdit } from 'features/fde'
import { DrawerState, useRightDrawer } from 'features/rightDrawer'
import React, { useState } from 'react'
import styled from 'styled-components'
import {
  GeckoJob,
  GeckoJobDataJSON,
  RolodexEntryPutJson,
  RolodexEntryType,
} from 'types'
import { v4 as uuid4 } from 'uuid'

import { TextSm } from './CDSTag'
import { ButtonGroup } from './JobCard'

const TextLg = styled.p`
  font-size: 1rem;
  line-height: 1.25rem;
`

export type JobCardHeaderProps = {
  job: GeckoJob
  forceRefreshInspections: () => void
  showAddInspection?: boolean
  showJobHandoff?: boolean
}

export const JobCardHeader: React.FC<JobCardHeaderProps> = ({
  job,
  forceRefreshInspections,
  showAddInspection = true,
  showJobHandoff = true,
}) => {
  const [messageApi, contextHolder] = useMessage()
  const { setDrawerEntry, setDrawerState, setOnDrawerClose } = useRightDrawer()
  const { rolodexPut } = useRolodexEdit()
  const { siteID } = useParams({ strict: false })
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 })
  const [popoverOpen, setPopoverOpen] = useState(false)

  const startDate = convertDateToString(job.data.start_date)

  const headerContent = job.data.name ? (
    <>
      <TextLg>
        <strong>{job.data.name}</strong>
      </TextLg>
      <TextSm>{startDate}</TextSm>
    </>
  ) : (
    <TextLg>{startDate}</TextLg>
  )

  const handleDuplicateOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    setPopupPosition({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    })
    setPopoverOpen(true)
  }

  const duplicateJobEntry = () => {
    setPopoverOpen(false)

    const newJobData: GeckoJobDataJSON = {
      start_date: job.data.start_date,
      end_date: job.data.end_date,
      scope: {
        ...job.data.scope,
      },
      safety: {
        ...job.data.safety,
      },
      site_details: {
        ...job.data.site_details,
      },
      name: `${job.data.name} (Copy)`,
    }
    const newJobEntry: RolodexEntryPutJson = {
      id: `job-${uuid4().slice(0, 6)}`,
      type: RolodexEntryType.JOB,
      data: newJobData,
      tags: {
        ...job.tags,
      },
    }
    rolodexPut([newJobEntry])
      .then(() => {
        messageApi.success('Job Entry duplicated successfully')
        forceRefreshInspections()
      })
      .catch(() => {
        messageApi.error('Failed to duplicate Job Entry')
      })
  }

  return (
    <>
      {contextHolder}
      <CantileverCard.CardHeader
        leadingContent={headerContent}
        trailingContent={
          <ButtonGroup>
            <CantileverCard.HiddenUnlessHovering>
              <InspectionOverviewAdminGate>
                {popoverOpen && (
                  <Popup
                    position={popupPosition}
                    message={`Are you sure you want to duplicate this job entry?`}
                    actions={
                      <>
                        <CantileverButton
                          primary
                          onClick={duplicateJobEntry}
                          title={'Duplicate Job Entry'}>
                          Duplicate
                        </CantileverButton>
                        <CantileverButton onClick={() => setPopoverOpen(false)}>
                          Cancel
                        </CantileverButton>
                      </>
                    }
                  />
                )}
                <CantileverButton
                  onClick={(e) => handleDuplicateOnClick(e)}
                  title={'Duplicate Job Entry'}>
                  <CopyIcon />
                </CantileverButton>
              </InspectionOverviewAdminGate>
            </CantileverCard.HiddenUnlessHovering>
            <CantileverCard.HiddenUnlessHovering>
              <InspectionOverviewAdminGate>
                <CantileverButton
                  onClick={() => {
                    setDrawerState(DrawerState.ImportGeckoInspection)
                    setDrawerEntry(job)
                    setOnDrawerClose(() => forceRefreshInspections)
                  }}
                  title={'Import Inspection'}>
                  <UploadIcon />
                </CantileverButton>
              </InspectionOverviewAdminGate>
            </CantileverCard.HiddenUnlessHovering>
            {showJobHandoff && (
              <InspectionOverviewViewerGate>
                <CantileverLinkButton
                  to='/job/$siteID'
                  params={{
                    siteID: siteID ?? '',
                  }}
                  search={{
                    mode: 'view',
                    jobId: job.id,
                  }}
                  title={'Job Handoff'}>
                  Job Handoff
                </CantileverLinkButton>
              </InspectionOverviewViewerGate>
            )}
            <InspectionOverviewAdminGate>
              {showAddInspection && (
                <AddButton
                  onClick={() => {
                    setDrawerEntry(job)
                    setDrawerState(DrawerState.EditGeckoInspection)
                  }}
                  entityName='Inspection'
                />
              )}
            </InspectionOverviewAdminGate>
          </ButtonGroup>
        }
      />
    </>
  )
}
